<template>
    <v-form ref="form" @submit.prevent="save">
        <v-card class="mb-5 pa-2">
            <card-header
                :title="
                    $t('bank-account.form.texts.title', {
                        'title-prefix': titlePrefix,
                    })
                "
            />
            <v-card-text>
                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-text-field
                            v-model="mutableModel.name"
                            :label="$t('bank-account.form.labels.name')"
                            :hint="$t('bank-account.form.hint.name')"
                            :counter="100"
                            :error-messages="nameErrors"
                            @input="$v.mutableModel.name.$touch()"
                            @blur="$v.mutableModel.name.$touch()"
                            autofocus
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-autocomplete
                            v-model="mutableModel.bank"
                            :items="banks"
                            :label="$t('bank-account.form.labels.bank')"
                            clearable
                            :item-text="itemText"
                            item-value="id"
                            return-object
                            :error-messages="bankErrors"
                            @input="$v.mutableModel.bank.$touch()"
                            @blur="$v.mutableModel.bank.$touch()"
                        ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            v-model="mutableModel.agency"
                            :label="$t('bank-account.form.labels.agency')"
                            :counter="10"
                            :error-messages="agencyErrors"
                            @input="$v.mutableModel.agency.$touch()"
                            @blur="$v.mutableModel.agency.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            v-model="mutableModel.account_number"
                            :label="
                                $t('bank-account.form.labels.account-number')
                            "
                            :hint="$t('bank-account.form.hint.account-number')"
                            :counter="20"
                            :error-messages="accountNumberErrors"
                            @input="$v.mutableModel.account_number.$touch()"
                            @blur="$v.mutableModel.account_number.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            v-model="mutableModel.document"
                            :label="$t('bank-account.form.labels.document')"
                            :hint="$t('bank-account.form.hint.document')"
                            :counter="18"
                            :error-messages="documentErrors"
                            @input="$v.mutableModel.document.$touch()"
                            @blur="$v.mutableModel.document.$touch()"
                        ></v-text-field>
                    </v-col>
                    <!-- <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            v-model="mutableModel.pix"
                            :label="$t('bank-account.form.labels.pix')"
                            :counter="100"
                            :error-messages="pixErrors"
                            @input="$v.mutableModel.pix.$touch()"
                            @blur="$v.mutableModel.pix.$touch()"
                        ></v-text-field>
                    </v-col> -->
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-label>{{
                            $t('bank-account.form.labels.active')
                        }}</v-label>
                        <v-radio-group v-model="mutableModel.active" row>
                            <v-radio
                                :label="$t('bank-account.form.labels.yes')"
                                :value="true"
                            ></v-radio>
                            <v-radio
                                :label="$t('bank-account.form.labels.no')"
                                :value="false"
                            ></v-radio>
                        </v-radio-group>
                    </v-col>
                </v-row>
            </v-card-text>
            <form-actions :loadingSave="loading" />
        </v-card>

        <confirm
            ref="confirmOneActive"
            :confirmText="$t('bank-account.dialogs.buttons.one-active')"
            confirmColor="green darken-1"
            width="500px"
        ></confirm>
    </v-form>
</template>

<script>
import CardHeader from '@/components/utils/CardHeader'
import Confirm from '@/components/dialogs/Confirm'
import FormActions from '@/components/utils/FormActions'
import { validationMixin } from 'vuelidate'
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'

import BaseModel from '@/models/BaseModel'
import Bank from '@/models/Bank'

export default {
    props: {
        model: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof BaseModel
            },
        },
        titlePrefix: String,
        reseller_id: {
            required: true,
        },
    },

    data() {
        return {
            loading: false,
            banks: null,
            amountBankAccounts: 0,
            mutableModel: this.model,
        }
    },

    components: {
        'card-header': CardHeader,
        'form-actions': FormActions,
        Confirm,
    },

    mixins: [validationMixin],

    validations() {
        return {
            mutableModel: {
                name: { required, maxLength: maxLength(100) },
                bank: {
                    requiredIf: requiredIf(() => {
                        return this.mutableModel.agency
                    }),
                },
                agency: {
                    requiredIf: requiredIf(() => {
                        return this.mutableModel.account_number
                    }),
                    maxLength: maxLength(10),
                },
                account_number: {
                    requiredIf: requiredIf(() => {
                        return this.mutableModel.agency
                    }),
                    maxLength: maxLength(20),
                },
                document: {
                    requiredIf: requiredIf(() => {
                        return this.mutableModel.agency
                    }),
                    maxLength: maxLength(18),
                },
                pix: { maxLength: maxLength(100) },
            },
        }
    },

    mounted() {
        this.init()
    },

    methods: {
        async save() {
            let confirm = await this.confirmOneActive()
            if (!confirm) {
                return
            }

            this.loading = true

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.validation-error'),
                )

                this.loading = false
                return
            }

            let clone = this.mutableModel.clone()
            clone.reseller_id = this.reseller_id
            clone.bank_id = this.mutableModel.bank.id
            clone.deleted_at = this.mutableModel.active
                ? null
                : this.$moment.now()

            let res = null

            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$emit('saved')

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('bank-account.form.bus.save-success', {
                    'bank-account': clone.name,
                }),
            )
        },

        clear() {
            this.$refs.form.reset()
        },

        async getBanks(page = 1) {
            let banks = await this.bankModel.paginate(50, page).catch(() => {})

            if (!banks) return

            let data = banks.data

            if (banks.meta.pagination.total_pages > page) {
                let more = await this.getBanks(page + 1)
                data = data.concat(more)
            }

            return data
        },

        async init() {
            let banks = await this.getBanks()
            this.banks = banks

            this.getAmountBankAccounts()
        },

        itemText(item) {
            return `${item.code} - ${item.name}`
        },

        async confirmOneActive() {
            if (
                (!this.mutableModel.id && this.amountBankAccounts > 0) ||
                (this.mutableModel.id && this.amountBankAccounts > 1)
            ) {
                let confirm = await this.$refs.confirmOneActive.open(
                    this.$t('bank-account.dialogs.messages.one-active.title'),
                    this.$t('bank-account.dialogs.messages.one-active.text'),
                )

                return confirm
            }

            return true
        },

        async getAmountBankAccounts() {
            let url = `${this.mutableModel.baseUrl()}/count/${this.mutableModel.resourceName()}/${
                this.reseller_id
            }`

            let res = await this.$http.get(url).catch()

            this.amountBankAccounts = res ? res.data : 0
        },
    },

    computed: {
        nameErrors() {
            const errors = []
            if (!this.$v.mutableModel.name.$dirty) return errors

            !this.$v.mutableModel.name.maxLength &&
                errors.push(this.$t('bank-account.form.errors.name-max-length'))
            !this.$v.mutableModel.name.required &&
                errors.push(this.$t('bank-account.form.errors.name-required'))

            return errors
        },

        bankErrors() {
            const errors = []
            if (!this.$v.mutableModel.bank.$dirty) return errors

            !this.$v.mutableModel.bank.requiredIf &&
                errors.push(
                    this.$t('bank-account.form.errors.bank-id-required-if'),
                )

            return errors
        },

        agencyErrors() {
            const errors = []
            if (!this.$v.mutableModel.agency.$dirty) return errors

            !this.$v.mutableModel.agency.requiredIf &&
                errors.push(
                    this.$t('bank-account.form.errors.agency-required-if'),
                )
            !this.$v.mutableModel.agency.maxLength &&
                errors.push(
                    this.$t('bank-account.form.errors.agency-max-length'),
                )

            return errors
        },

        accountNumberErrors() {
            const errors = []
            if (!this.$v.mutableModel.account_number.$dirty) return errors

            !this.$v.mutableModel.account_number.requiredIf &&
                errors.push(
                    this.$t(
                        'bank-account.form.errors.account-number-required-if',
                    ),
                )
            !this.$v.mutableModel.account_number.maxLength &&
                errors.push(
                    this.$t(
                        'bank-account.form.errors.account-number-max-length',
                    ),
                )

            return errors
        },

        documentErrors() {
            const errors = []
            if (!this.$v.mutableModel.document.$dirty) return errors

            !this.$v.mutableModel.document.requiredIf &&
                errors.push(
                    this.$t('bank-account.form.errors.document-required-if'),
                )
            !this.$v.mutableModel.document.maxLength &&
                errors.push(
                    this.$t('bank-account.form.errors.document-max-length'),
                )

            return errors
        },

        pixErrors() {
            const errors = []
            if (!this.$v.mutableModel.pix.$dirty) return errors

            !this.$v.mutableModel.pix.maxLength &&
                errors.push(this.$t('bank-account.form.errors.pix-max-length'))

            return errors
        },

        bankModel() {
            return new Bank()
        },
    },
}
</script>
